/**
 * ScopeFilterBuilder (#1867 PR2a) — the visual condition builder for the
 * app's issue-source scope filter. Field → operator → value(s), chained by a
 * Match ALL / ANY selector; an Advanced (raw text) escape; a live approximate
 * match count. It owns a `ScopeFilterModel` internally (seeded once from the
 * stored `scopeFilter`) and emits the compiled fragment via `onChange`. All
 * compile/parse goes through the tracker's translator, so the
 * builder and the Advanced view are two views of one string — and text the
 * flat builder can't model stays in Advanced, never silently rewritten.
 *
 * The fields, and the language's name, come from the translator; the value
 * editor is chosen by each field's value kind (a fixed list, discovered
 * versions or priorities, label or people type-ahead, or a date).
 *
 * Label, people and version lookups read the selected projects as they are
 * on screen, saved or not, and run again when the selection changes. A reply
 * that arrives for an earlier selection is thrown away. For a translator that
 * checks saved values (GitHub), each label and version name in the filter is
 * checked against the selected projects and marked "not found" or "couldn't
 * check"; the filter itself is never changed by the check.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { Label, SearchableSelect } from '@/components/ui'
import { applicationsAPI } from '@/lib/api'
import {
  isConditionComplete,
  fieldConfig,
  operatorsForField,
  type ScopeFilterModel,
  type ScopeFilterTranslator,
  type Condition,
  type OperatorKey,
} from '@/lib/scopeFilter'

const PREVIEW_DEBOUNCE_MS = 400

interface Props {
  /** The tracker's filter language: its fields and both directions. */
  translator: ScopeFilterTranslator
  /** The tracker's word for its projects, plural ("projects"). */
  projectsWord: string
  appId: string
  connectionId: string
  projectKeys: string[]
  /** Mapped requirement + defect type ids — the preview's scope, so "matches
   * N" reflects exactly what will sync. */
  issueTypeIds: string[]
  value: string
  onChange: (scopeFilter: string) => void
  /** Reports whether the filter is safe to save (no incomplete condition, no
   * server-rejected filter) so the card can gate its Save button. */
  onValidityChange?: (valid: boolean) => void
  /** Seed for the accountId → displayName map (from the binding's cache), so a
   * saved people filter shows names on reload. */
  initialUserNames?: Record<string, string>
  /** Reports the accountId → displayName map for people currently in the
   * filter, so the card can persist it (`userDisplayNames`). */
  onUserNamesChange?: (names: Record<string, string>) => void
  /** What the mapping sorts by, for the help line ("type", or "label" for a
   * tracker that sorts by label). */
  classifierWord?: string
}

/** A saved value's check result, when it needs a mark. */
type ValueMark = 'missing' | 'unknown'

type PreviewState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ok'; count: number | null }
  | { status: 'error'; message: string }

export function ScopeFilterBuilder({
  translator,
  projectsWord,
  appId,
  connectionId,
  projectKeys,
  issueTypeIds,
  value,
  onChange,
  onValidityChange,
  initialUserNames,
  onUserNamesChange,
  classifierWord = 'type',
}: Props) {
  // Seeded ONCE from the stored filter; the builder owns it thereafter. The
  // card remounts this (via a connectionId key) when the connection changes.
  const [model, setModel] = useState<ScopeFilterModel>(() => translator.parse(value))
  const [advancedUnrepresentable, setAdvancedUnrepresentable] = useState(false)

  const [versions, setVersions] = useState<string[] | null>(null)
  const [priorities, setPriorities] = useState<string[] | null>(null)
  const [preview, setPreview] = useState<PreviewState>({ status: 'idle' })
  // accountId → displayName for people in the filter (seeded from the binding,
  // grown from user-search selections).
  const [nameMap, setNameMap] = useState<Record<string, string>>(() => initialUserNames ?? {})

  const compiled = useMemo(() => translator.compile(model), [model, translator])
  const projectsKey = projectKeys.join(',')
  const typesKey = issueTypeIds.join(',')
  const cfgOf = (key: string) => fieldConfig(translator, key)
  /** The default operator for a field — its first (operators are per-field,
   * so switching field must reset to one the new field actually offers). */
  const defaultOpFor = (field: string): OperatorKey => operatorsForField(cfgOf(field))[0]?.key ?? 'isAnyOf'

  // Emit the compiled fragment whenever it changes.
  useEffect(() => {
    onChange(compiled)
  }, [compiled]) // eslint-disable-line react-hooks/exhaustive-deps

  const complete = model.mode === 'advanced' ? true : model.conditions.every(isConditionComplete)

  useEffect(() => {
    onValidityChange?.(complete && preview.status !== 'error')
  }, [complete, preview.status]) // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced, cancel-superseded preview.
  const reqIdRef = useRef(0)
  useEffect(() => {
    if (!complete || projectKeys.length === 0) {
      setPreview({ status: 'idle' })
      return
    }
    const id = ++reqIdRef.current
    setPreview({ status: 'loading' })
    const timer = setTimeout(() => {
      applicationsAPI
        .previewIssueSourceFilter(appId, { connectionId, projectKeys, issueTypeIds, scopeFilter: compiled })
        .then(({ count }) => {
          if (id === reqIdRef.current) setPreview({ status: 'ok', count })
        })
        .catch((err) => {
          if (id === reqIdRef.current) {
            setPreview({ status: 'error', message: err instanceof Error ? err.message : "That filter isn't valid." })
          }
        })
    }, PREVIEW_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [compiled, complete, appId, connectionId, projectsKey, typesKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // Discovered value lists — loaded lazily when a condition needs them.
  const needsKind = (kind: string) =>
    model.mode === 'builder' && model.conditions.some((c) => cfgOf(c.field)?.valueKind === kind && c.op !== 'isEmpty')
  const needsVersions = needsKind('versions')
  const needsPriorities = needsKind('priorities')
  // Versions are per (connection, projects) — invalidate on a project change.
  useEffect(() => setVersions(null), [projectsKey])
  useEffect(() => {
    if (!needsVersions || versions !== null || projectKeys.length === 0) return
    // A reply for a selection that has since changed is thrown away.
    let stale = false
    applicationsAPI
      .getIssueSourceVersions(appId, connectionId, projectKeys)
      .then((vs) => { if (!stale) setVersions(vs.map((v) => v.name)) })
      .catch(() => { if (!stale) setVersions([]) })
    return () => { stale = true }
  }, [needsVersions, versions, appId, connectionId, projectsKey]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!needsPriorities || priorities !== null) return
    applicationsAPI
      .getIssueSourcePriorities(appId, connectionId)
      .then((ps) => setPriorities(ps.map((p) => p.name)))
      .catch(() => setPriorities([]))
  }, [needsPriorities, priorities, appId, connectionId]) // eslint-disable-line react-hooks/exhaustive-deps

  const optionsFor = useCallback(
    (field: string): { options: string[] | null } => {
      const cfg = fieldConfig(translator, field)
      if (cfg?.valueKind === 'fixed') return { options: [...(cfg.options ?? [])] }
      if (cfg?.valueKind === 'versions') return { options: versions }
      return { options: priorities }
    },
    [versions, priorities, translator],
  )

  // Async loaders for the type-ahead fields (labels / people), over the
  // selected projects. With none selected there is nothing to suggest.
  const loadLabels = useCallback(
    (query: string) => projectKeys.length === 0
      ? Promise.resolve([])
      : applicationsAPI.suggestIssueSourceLabels(appId, connectionId, query, projectKeys).then((ls) => ls.map((l) => ({ value: l, label: l }))),
    [appId, connectionId, projectsKey], // eslint-disable-line react-hooks/exhaustive-deps
  )
  const loadUsers = useCallback(
    (query: string) => projectKeys.length === 0
      ? Promise.resolve([])
      : applicationsAPI.searchIssueSourceUsers(appId, connectionId, query, projectKeys).then((us) => us.map((u) => ({ value: u.accountId, label: u.displayName }))),
    [appId, connectionId, projectsKey], // eslint-disable-line react-hooks/exhaustive-deps
  )

  // Saved label and version names, checked against the selected projects
  // when the filter opens and again when the selection changes. Only names
  // that need a mark are kept; a name the check found shows as usual.
  const [valueMarks, setValueMarks] = useState<Record<string, ValueMark>>({})
  const modelRef = useRef(model)
  modelRef.current = model
  const checkId = useRef(0)
  useEffect(() => {
    if (!translator.checksSavedValues) return
    const id = ++checkId.current
    const current = modelRef.current
    const byKind: Record<'labels' | 'versions', Set<string>> = { labels: new Set(), versions: new Set() }
    if (current.mode === 'builder') {
      for (const c of current.conditions) {
        const kind = fieldConfig(translator, c.field)?.valueKind
        if ((kind === 'labels' || kind === 'versions') && c.op !== 'isEmpty') for (const v of c.values) byKind[kind].add(v)
      }
    } else if (translator.savedValueNames) {
      // A filter shown as text: the names it writes, read without rewriting it.
      const named = translator.savedValueNames(current.text)
      for (const n of named.labels) byKind.labels.add(n)
      for (const n of named.versions) byKind.versions.add(n)
    }
    if (projectKeys.length === 0 || (byKind.labels.size === 0 && byKind.versions.size === 0)) {
      setValueMarks({})
      return
    }
    const timer = setTimeout(() => {
      Promise.all((['labels', 'versions'] as const).filter((k) => byKind[k].size > 0).map(async (field) => {
        const names = [...byKind[field]]
        try {
          const result = await applicationsAPI.checkIssueSourceFilterValues(appId, { connectionId, projectKeys, field, names })
          return names.map((n) => [`${field}:${n}`, result[n] ?? 'unknown'] as const)
        } catch {
          return names.map((n) => [`${field}:${n}`, 'unknown'] as const)
        }
      })).then((lists) => {
        if (id !== checkId.current) return
        const marks: Record<string, ValueMark> = {}
        for (const [key, status] of lists.flat()) if (status !== 'found') marks[key] = status
        setValueMarks(marks)
      })
    }, PREVIEW_DEBOUNCE_MS)
    return () => clearTimeout(timer)
    // Advanced text is checked again after each pause in typing.
  }, [appId, connectionId, projectsKey, translator, model.mode === 'advanced' ? model.text : '']) // eslint-disable-line react-hooks/exhaustive-deps
  const markOf = (kind: string) => (v: string): ValueMark | undefined => valueMarks[`${kind}:${v}`]
  const recordName = useCallback((accountId: string, name: string) => {
    setNameMap((m) => (m[accountId] === name ? m : { ...m, [accountId]: name }))
  }, [])

  // Report the display names for the people currently in the filter, for save.
  useEffect(() => {
    if (!onUserNamesChange) return
    const used: Record<string, string> = {}
    if (model.mode === 'builder') {
      for (const c of model.conditions) {
        if (cfgOf(c.field)?.valueKind !== 'user') continue
        for (const id of c.values) if (nameMap[id]) used[id] = nameMap[id]
      }
    }
    onUserNamesChange(used)
  }, [compiled, nameMap]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── model mutators (builder mode) ──
  const conditions = model.mode === 'builder' ? model.conditions : []
  const match = model.mode === 'builder' ? model.match : 'all'
  const setConditions = (next: Condition[]) => setModel({ mode: 'builder', match, conditions: next })
  const updateCondition = (i: number, patch: Partial<Condition>) =>
    setConditions(conditions.map((c, idx) => (idx === i ? { ...c, ...patch } : c)))
  const addCondition = () => {
    const first = translator.fields[0]?.key
    if (first) setConditions([...conditions, { field: first, op: defaultOpFor(first), values: [] }])
  }
  const removeCondition = (i: number) => setConditions(conditions.filter((_, idx) => idx !== i))

  // Pick the value editor by the field's value kind (isEmpty needs none).
  const renderValueEditor = (c: Condition, i: number): ReactNode => {
    if (c.op === 'isEmpty') return null
    const setValues = (values: string[]) => updateCondition(i, { values })
    const kind = cfgOf(c.field)?.valueKind
    if (kind === 'date') {
      return <RelativeOrDateInput op={c.op} value={c.values[0] ?? ''} onChange={(v) => setValues(v ? [v] : [])} />
    }
    if (kind === 'labels') {
      return <AsyncMultiValuePicker selected={c.values} onChange={setValues} loadOptions={loadLabels} reloadKey={projectsKey} renderLabel={(v) => v} markOf={markOf('labels')} allowFreeText placeholder="+ label" />
    }
    if (kind === 'user') {
      return (
        <AsyncMultiValuePicker
          selected={c.values}
          onChange={setValues}
          loadOptions={loadUsers}
          reloadKey={projectsKey}
          onSelectOption={(o) => recordName(o.value, o.label)}
          renderLabel={(v) => nameMap[v] ?? (translator.peopleValuesReadable ? v : 'Unknown user')}
          placeholder="+ person"
        />
      )
    }
    if (c.op === 'is') {
      return <SingleValueSelect value={c.values[0] ?? ''} onChange={(v) => setValues(v ? [v] : [])} options={optionsFor(c.field).options ?? []} label={`${cfgOf(c.field)?.label ?? 'Field'} value`} />
    }
    return (
      <MultiValuePicker
        selected={c.values}
        onChange={setValues}
        options={optionsFor(c.field).options}
        label={`${cfgOf(c.field)?.label ?? 'Field'} values`}
        placeholder={cfgOf(c.field)?.addPlaceholder}
        markOf={kind === 'versions' ? markOf('versions') : undefined}
      />
    )
  }

  const toAdvanced = () => setModel({ mode: 'advanced', text: translator.compile(model) })
  const tryBackToBuilder = () => {
    const parsed = translator.parse(model.mode === 'advanced' ? model.text : compiled)
    if (parsed.mode === 'builder') {
      setAdvancedUnrepresentable(false)
      setModel(parsed)
    } else {
      setAdvancedUnrepresentable(true) // can't show as a visual rule — stay in Advanced
    }
  }

  return (
    <div className="space-y-2">
      <Label>Which issues should be in play?</Label>

      {model.mode === 'advanced' ? (
        <div className="space-y-1.5">
          <textarea
            value={model.text}
            onChange={(e) => {
              setAdvancedUnrepresentable(false)
              setModel({ mode: 'advanced', text: e.target.value })
            }}
            rows={2}
            placeholder={`Leave empty to include every mapped ${classifierWord} in the ${projectsWord} above.`}
            className="w-full rounded-md border border-border bg-background px-2.5 py-2 font-mono text-xs"
            aria-label={`Scope filter (${translator.languageName})`}
          />
          {advancedUnrepresentable && (
            <p className="text-[11px] text-muted-foreground">This filter is edited as {translator.languageName} — it can’t be shown as a visual rule.</p>
          )}
          <AdvancedValueMarks marks={valueMarks} />
          <button type="button" onClick={tryBackToBuilder} className="text-xs text-primary hover:underline">
            ← Back to the visual builder
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <span>Match</span>
            <select
              value={match}
              onChange={(e) => setModel({ mode: 'builder', match: e.target.value as 'all' | 'any', conditions })}
              aria-label="Match all or any"
              className="rounded-md border border-border bg-background px-2 py-1 text-xs"
            >
              <option value="all">ALL</option>
              <option value="any">ANY</option>
            </select>
            <span className="text-muted-foreground text-xs">of these conditions</span>
          </div>

          {conditions.map((c, i) => (
            <ConditionRow
              key={i}
              translator={translator}
              condition={c}
              valueEditor={renderValueEditor(c, i)}
              onField={(field) => updateCondition(i, { field, op: defaultOpFor(field), values: [] })}
              onOp={(op) => updateCondition(i, { op, ...(op === 'isEmpty' ? { values: [] } : {}) })}
              onRemove={() => removeCondition(i)}
            />
          ))}

          <button
            type="button"
            onClick={addCondition}
            className="rounded-md border border-dashed border-primary/50 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/5"
          >
            + Add condition
          </button>

          <button type="button" onClick={toAdvanced} className="block text-xs text-muted-foreground hover:text-foreground">
            Advanced — edit as {translator.languageName}
          </button>
          {compiled && (
            <p className="rounded-md bg-muted px-2.5 py-1.5 font-mono text-[11px] text-muted-foreground">
              <span className="font-sans font-medium text-foreground">This rule becomes: </span>
              {compiled}
            </p>
          )}
        </div>
      )}

      <PreviewLine preview={preview} incomplete={!complete} />
      <p className="text-[11px] text-muted-foreground">
        Leave empty to include every mapped {classifierWord} in the {projectsWord} above. This same filter drives the issue list, import, and automatic sync.
      </p>
    </div>
  )
}

// ── one condition row ──
function ConditionRow({
  translator,
  condition,
  valueEditor,
  onField,
  onOp,
  onRemove,
}: {
  translator: ScopeFilterTranslator
  condition: Condition
  valueEditor: ReactNode
  onField: (f: string) => void
  onOp: (o: OperatorKey) => void
  onRemove: () => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-background px-2.5 py-2">
      <select
        value={condition.field}
        onChange={(e) => onField(e.target.value)}
        aria-label="Field"
        className="rounded-md border border-border bg-background px-2 py-1 text-xs font-medium"
      >
        {translator.fields.map((f) => (
          <option key={f.key} value={f.key}>{f.label}</option>
        ))}
      </select>
      <select
        value={condition.op}
        onChange={(e) => onOp(e.target.value as OperatorKey)}
        aria-label="Comparison"
        className="rounded-md border border-border bg-background px-2 py-1 text-xs"
      >
        {operatorsForField(fieldConfig(translator, condition.field)).map((o) => (
          <option key={o.key} value={o.key}>{o.label}</option>
        ))}
      </select>
      {valueEditor}
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove condition"
        className="ml-auto text-muted-foreground hover:text-foreground text-base leading-none px-1"
      >
        ×
      </button>
    </div>
  )
}

// ── selected-value chips (shared by both pickers) ──
const MARK_TEXT: Record<ValueMark, { suffix: string; title: string; cls: string }> = {
  missing: {
    suffix: ' · not found',
    title: 'No selected repository has this.',
    cls: 'border-amber-400/60 bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  },
  unknown: {
    suffix: ' · couldn’t check',
    title: 'A repository couldn’t be checked just now.',
    cls: 'border-border bg-muted text-muted-foreground',
  },
}

function Chips({
  selected, renderLabel, onRemove, markOf,
}: {
  selected: string[]
  renderLabel?: (v: string) => string
  onRemove: (v: string) => void
  /** A saved value's check result, shown on its chip. */
  markOf?: (v: string) => ValueMark | undefined
}) {
  const label = renderLabel ?? ((v: string) => v)
  return (
    <>
      {selected.map((v) => {
        const mark = markOf?.(v)
        const m = mark ? MARK_TEXT[mark] : undefined
        return (
          <span
            key={v}
            title={m?.title}
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${m ? m.cls : 'border-primary/40 bg-primary/10 text-primary'}`}
          >
            {label(v)}{m?.suffix}
            <button type="button" onClick={() => onRemove(v)} aria-label={`Remove ${label(v)}`} className="opacity-70 hover:opacity-100">×</button>
          </span>
        )
      })}
    </>
  )
}

// ── marks for the names in a filter shown as text ──
/** The label and milestone names in an Advanced filter that the check
 * couldn't find or couldn't read, beside the text box. Only names needing a
 * mark are listed; the text itself is left as typed. */
function AdvancedValueMarks({ marks }: { marks: Record<string, ValueMark> }) {
  const entries = Object.entries(marks)
  if (entries.length === 0) return null
  return (
    <div className="flex flex-wrap items-center gap-1.5" aria-label="Checked names">
      {entries.map(([key, mark]) => {
        const name = key.slice(key.indexOf(':') + 1)
        const m = MARK_TEXT[mark]
        return (
          <span key={key} title={m.title} className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] ${m.cls}`}>
            {name}{m.suffix}
          </span>
        )
      })}
    </div>
  )
}

// ── one value from a short fixed list (State, Closed as) ──
function SingleValueSelect({
  value, onChange, options, label,
}: {
  value: string
  onChange: (v: string) => void
  options: string[]
  label: string
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={label}
      className="rounded-md border border-border bg-background px-2 py-1 text-xs"
    >
      <option value="">Pick one…</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  )
}

// ── chips + constrained add-picker ──
function MultiValuePicker({
  selected,
  onChange,
  options,
  label,
  placeholder,
  markOf,
}: {
  selected: string[]
  onChange: (v: string[]) => void
  options: string[] | null
  label: string
  placeholder?: string
  markOf?: (v: string) => ValueMark | undefined
}) {
  if (options === null) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" /> loading…
      </span>
    )
  }
  const remaining = options.filter((o) => !selected.includes(o))
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Chips selected={selected} markOf={markOf} onRemove={(v) => onChange(selected.filter((s) => s !== v))} />
      <SearchableSelect
        value=""
        onChange={(v) => { if (v) onChange([...selected, v]) }}
        options={remaining.map((o) => ({ value: o, label: o }))}
        placeholder={placeholder ?? '+ value'}
        searchPlaceholder="Search…"
        aria-label={label}
        disabled={remaining.length === 0}
      />
    </div>
  )
}

// ── async type-ahead multi-value picker (labels + people) ──
interface AsyncOption { value: string; label: string }
function AsyncMultiValuePicker({
  selected,
  onChange,
  loadOptions,
  renderLabel,
  onSelectOption,
  allowFreeText,
  placeholder,
  reloadKey,
  markOf,
}: {
  selected: string[]
  onChange: (v: string[]) => void
  loadOptions: (query: string) => Promise<AsyncOption[]>
  renderLabel: (value: string) => string
  onSelectOption?: (opt: AsyncOption) => void
  allowFreeText?: boolean
  placeholder?: string
  /** Changes when what the search reads changes (the selected projects):
   * the search runs again, and a reply for the old one is thrown away. */
  reloadKey?: string
  markOf?: (v: string) => ValueMark | undefined
}) {
  const [query, setQuery] = useState('')
  const [options, setOptions] = useState<AsyncOption[]>([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const reqId = useRef(0)

  // What the search reads changed (the selected projects): the options
  // listed are from the old selection, so they go at once, before the new
  // search replies, and can't be picked meanwhile.
  const lastReloadKey = useRef(reloadKey)
  if (lastReloadKey.current !== reloadKey) {
    lastReloadKey.current = reloadKey
    if (options.length > 0) setOptions([])
  }

  // Debounced + cancel-superseded search.
  useEffect(() => {
    const q = query.trim()
    if (!q) {
      // Cleared: any reply still on its way is for an old search.
      reqId.current++
      setOptions([])
      setLoading(false)
      return
    }
    const id = ++reqId.current
    setLoading(true)
    const timer = setTimeout(() => {
      loadOptions(q)
        .then((opts) => {
          if (id === reqId.current) {
            setOptions(opts)
            setLoading(false)
          }
        })
        .catch(() => {
          if (id === reqId.current) {
            setOptions([])
            setLoading(false)
          }
        })
    }, 300)
    return () => clearTimeout(timer)
  }, [query, reloadKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const add = (opt: AsyncOption) => {
    if (!selected.includes(opt.value)) {
      onChange([...selected, opt.value])
      onSelectOption?.(opt)
    }
    setQuery('')
    setOptions([])
    setOpen(false)
  }
  const suggestions = options.filter((o) => !selected.includes(o.value))
  const trimmed = query.trim()
  const showFreeText = !!allowFreeText && trimmed.length > 0 && !suggestions.some((s) => s.value === trimmed)

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Chips selected={selected} renderLabel={renderLabel} markOf={markOf} onRemove={(v) => onChange(selected.filter((s) => s !== v))} />
      <div className="relative">
        <input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={placeholder ?? '+ value'}
          aria-label={placeholder ?? 'Search'}
          className="w-32 rounded-md border border-border bg-background px-2 py-1 text-xs"
        />
        {open && (loading || suggestions.length > 0 || showFreeText) && (
          <div className="absolute z-10 mt-1 max-h-48 w-56 overflow-auto rounded-md border border-border bg-background shadow-md">
            {loading && <div className="px-2 py-1 text-[11px] text-muted-foreground">Searching…</div>}
            {suggestions.map((o) => (
              <button key={o.value} type="button" onMouseDown={(e) => { e.preventDefault(); add(o) }} className="block w-full truncate px-2 py-1 text-left text-xs hover:bg-muted">
                {o.label}
              </button>
            ))}
            {showFreeText && (
              <button type="button" onMouseDown={(e) => { e.preventDefault(); add({ value: trimmed, label: trimmed }) }} className="block w-full px-2 py-1 text-left text-xs hover:bg-muted">
                Add “{trimmed}”
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ── relative-or-absolute date value editor (created / updated) ──
function RelativeOrDateInput({ op, value, onChange }: { op: OperatorKey; value: string; onChange: (v: string) => void }) {
  if (op === 'inLast') {
    const m = /^-(\d+)([dw])$/.exec(value)
    const n = m ? m[1] : ''
    const unit = m ? m[2] : 'd'
    const emit = (nn: string, uu: string) => onChange(nn && Number(nn) > 0 ? `-${nn}${uu}` : '')
    return (
      <span className="inline-flex items-center gap-1">
        <input
          type="number"
          min={1}
          value={n}
          onChange={(e) => emit(e.target.value, unit)}
          placeholder="30"
          aria-label="Amount"
          className="w-16 rounded-md border border-border bg-background px-2 py-1 text-xs"
        />
        <select value={unit} onChange={(e) => emit(n, e.target.value)} aria-label="Unit" className="rounded-md border border-border bg-background px-2 py-1 text-xs">
          <option value="d">days</option>
          <option value="w">weeks</option>
        </select>
      </span>
    )
  }
  return (
    <input
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label="Date"
      className="rounded-md border border-border bg-background px-2 py-1 text-xs"
    />
  )
}

// ── the live "matches N" line ──
function PreviewLine({ preview, incomplete }: { preview: PreviewState; incomplete: boolean }) {
  if (incomplete) {
    return <p className="text-xs text-amber-700 dark:text-amber-400">Finish each condition (pick a value) to see how many issues match.</p>
  }
  if (preview.status === 'loading') {
    return <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Checking…</p>
  }
  if (preview.status === 'error') {
    return <p className="text-xs text-destructive" role="alert">✕ {preview.message}</p>
  }
  if (preview.status === 'ok') {
    if (preview.count === null) return null
    if (preview.count === 0) {
      return <p className="text-xs text-amber-700 dark:text-amber-400">⚠ This filter matches 0 issues right now.</p>
    }
    return <p className="text-xs text-green-700 dark:text-green-400">✓ About {preview.count.toLocaleString()} {preview.count === 1 ? 'issue' : 'issues'} match right now.</p>
  }
  return null
}
